import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Timestamp } from '@bufbuild/protobuf';
import { Code, ConnectError } from '@connectrpc/connect';
import { SpecraButton, SpecraFormField, SpecraInput, SpecraSelect } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { RegistryContractDecision, RegistryContractFactKey, RegistryContractFactValue, RegistryContractIdentity, RegistryContractRefs } from '../../../../../../../gen/ts/mono/datagateway/v1/registry_contract_pb';
import { contractsClient, loadAssociationOptions, loadRateOptions } from './api';
import { contractQueryKey } from './ContractsTab';
import { identityLabel, measures } from './facts';
const ids = Testids.registryContracts;
function Field({ label, children }) {
    return /*#__PURE__*/ _jsx(SpecraFormField, {
        label: label,
        children: children
    });
}
function Picker({ label, value, onChange, options, required = false }) {
    return /*#__PURE__*/ _jsx(Field, {
        label: label,
        children: /*#__PURE__*/ _jsxs(SpecraSelect, {
            value: value,
            required: required,
            onChange: (e)=>onChange(e.target.value),
            children: [
                /*#__PURE__*/ _jsx("option", {
                    value: "",
                    children: "Unresolved / no association"
                }),
                value && !options.some((o)=>o.id === value) && /*#__PURE__*/ _jsx("option", {
                    value: value,
                    children: "Existing association (unavailable)"
                }),
                options.map((o)=>/*#__PURE__*/ _jsx("option", {
                        value: o.id,
                        children: o.label
                    }, o.id))
            ]
        })
    });
}
function Failure({ error }) {
    if (!error) return null;
    return /*#__PURE__*/ _jsx("p", {
        role: "alert",
        children: error instanceof ConnectError && error.code === Code.PermissionDenied ? 'Editing is not permitted. Your changes were not saved.' : error instanceof ConnectError && error.code === Code.InvalidArgument ? 'Unresolved identity or fact context. Check agency, parent, role, scope, period, unit and value; nothing was silently matched by PIID.' : 'Save could not be confirmed. Retry the same decision to safely check it.'
    });
}
export function ContractForm({ viewer, contract, onCancel, onSaved }) {
    const [identity, setIdentity] = useState(()=>new RegistryContractIdentity(contract?.identity));
    const [refs, setRefs] = useState(()=>new RegistryContractRefs(contract?.refs));
    const [rationale, setRationale] = useState('');
    const [parentSearch, setParentSearch] = useState('');
    const catalog = useQuery({
        queryKey: contractQueryKey(viewer, 'associations'),
        queryFn: ()=>loadAssociationOptions(viewer.tenant),
        placeholderData: undefined,
        retry: false
    });
    const parentQuery = useInfiniteQuery({
        queryKey: contractQueryKey(viewer, 'parent-search', parentSearch),
        queryFn: ({ pageParam })=>contractsClient.browseRegistryContracts({
                piid: parentSearch,
                afterId: pageParam,
                limit: 30
            }),
        initialPageParam: '',
        getNextPageParam: (p)=>p.nextAfterId || undefined,
        enabled: !!parentSearch,
        placeholderData: undefined,
        retry: false
    });
    const save = useMutation({
        mutationFn: async ()=>contract ? contractsClient.updateRegistryContractRefs({
                id: contract.id,
                refs,
                rationale
            }) : contractsClient.createRegistryContract({
                identity,
                refs
            }),
        onSuccess: (r)=>{
            if (r.contract) onSaved(r.contract);
        },
        retry: false
    });
    const setIdentityField = (key, value)=>setIdentity(new RegistryContractIdentity({
            ...identity,
            [key]: value
        }));
    const setRef = (key, value)=>setRefs(new RegistryContractRefs({
            ...refs,
            [key]: value
        }));
    return /*#__PURE__*/ _jsxs("form", {
        "data-testid": ids.form,
        className: "space-y-3 rounded border p-4",
        onSubmit: (e)=>{
            e.preventDefault();
            save.mutate();
        },
        children: [
            /*#__PURE__*/ _jsx("h3", {
                children: contract ? 'Edit manual associations' : 'Create a contextual contract identity'
            }),
            !contract && /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    [
                        [
                            'piid',
                            'PIID'
                        ],
                        [
                            'agency',
                            'Source agency identifier'
                        ],
                        [
                            'subtier',
                            'Source subtier identifier'
                        ],
                        [
                            'referencedIdv',
                            'Referenced IDV / parent source identifier'
                        ],
                        [
                            'partyKey',
                            'Party identity (for subcontract workshare)'
                        ]
                    ].map(([key, label])=>/*#__PURE__*/ _jsx(Field, {
                            label: label,
                            children: /*#__PURE__*/ _jsx(SpecraInput, {
                                required: key === 'piid' || key === 'agency' || key === 'subtier',
                                value: identity[key],
                                onChange: (e)=>setIdentityField(key, e.target.value)
                            })
                        }, key)),
                    /*#__PURE__*/ _jsx(Picker, {
                        label: "Record kind",
                        value: identity.kind,
                        required: true,
                        onChange: (v)=>setIdentityField('kind', v),
                        options: [
                            'vehicle_mechanism',
                            'holder_contract',
                            'task_order',
                            'delivery_order'
                        ].map((v)=>({
                                id: v,
                                label: v.replace(/_/g, ' ')
                            }))
                    }),
                    /*#__PURE__*/ _jsx(Picker, {
                        label: "Award role",
                        value: identity.role,
                        required: true,
                        onChange: (v)=>setIdentityField('role', v),
                        options: [
                            'prime',
                            'subcontract'
                        ].map((v)=>({
                                id: v,
                                label: v
                            }))
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        children: "For orders supply the actual parent source identifier. For subcontracts supply the actual party identity. Missing context remains unresolved; a PIID is not a universal identity."
                    })
                ]
            }),
            catalog.isPending ? /*#__PURE__*/ _jsx("output", {
                children: "Loading canonical selectors…"
            }) : catalog.isError ? /*#__PURE__*/ _jsxs("p", {
                role: "alert",
                children: [
                    "Canonical selectors unavailable. Retry before saving associations.",
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        type: "button",
                        onClick: ()=>void catalog.refetch(),
                        children: "Retry selectors"
                    })
                ]
            }) : /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(Picker, {
                        label: "Awardee company",
                        value: refs.companyId,
                        onChange: (v)=>setRef('companyId', v),
                        options: catalog.data.companies
                    }),
                    /*#__PURE__*/ _jsx(Picker, {
                        label: "Awarding government organization",
                        value: refs.governmentOrgId,
                        onChange: (v)=>setRef('governmentOrgId', v),
                        options: catalog.data.agencies
                    }),
                    /*#__PURE__*/ _jsx(Picker, {
                        label: "Vehicle mechanism (not parent contract)",
                        value: refs.vehicleId,
                        onChange: (v)=>setRef('vehicleId', v),
                        options: catalog.data.vehicles
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(RatePicker, {
                viewer: viewer,
                value: refs.rateObservationId,
                selected: contract?.associatedRate ? {
                    id: contract.associatedRate.id,
                    label: contract.associatedRate.label
                } : undefined,
                onChange: (v)=>setRef('rateObservationId', v)
            }),
            /*#__PURE__*/ _jsx(Field, {
                label: "Find parent contract by PIID",
                children: /*#__PURE__*/ _jsx(SpecraInput, {
                    value: parentSearch,
                    onChange: (e)=>{
                        setParentSearch(e.target.value);
                    }
                })
            }),
            parentQuery.isFetching && /*#__PURE__*/ _jsx("output", {
                children: "Searching parent contracts…"
            }),
            parentQuery.isError && /*#__PURE__*/ _jsx("p", {
                role: "alert",
                children: "Parent search unavailable"
            }),
            /*#__PURE__*/ _jsx(Picker, {
                label: "Parent contract (distinct from Vehicle)",
                value: refs.parentId,
                onChange: (v)=>setRef('parentId', v),
                options: (parentQuery.data?.pages.flatMap((p)=>p.contracts) ?? []).filter((p)=>p.id !== contract?.id).map((p)=>({
                        id: p.id,
                        label: identityLabel(p.identity)
                    }))
            }),
            parentQuery.hasNextPage && /*#__PURE__*/ _jsx(SpecraButton, {
                type: "button",
                variant: "secondary",
                disabled: parentQuery.isFetchingNextPage,
                onClick: ()=>void parentQuery.fetchNextPage(),
                children: "More parent results"
            }),
            contract && /*#__PURE__*/ _jsx(Field, {
                label: "Association rationale",
                children: /*#__PURE__*/ _jsx(SpecraInput, {
                    required: true,
                    value: rationale,
                    onChange: (e)=>setRationale(e.target.value)
                })
            }),
            /*#__PURE__*/ _jsx("p", {
                children: "Manual association only; this does not verify holder eligibility."
            }),
            /*#__PURE__*/ _jsx(Failure, {
                error: save.error
            }),
            /*#__PURE__*/ _jsx(SpecraButton, {
                type: "submit",
                "data-testid": ids.save,
                disabled: save.isPending || !catalog.data,
                children: save.isPending ? 'Saving…' : 'Save contract'
            }),
            /*#__PURE__*/ _jsx(SpecraButton, {
                type: "button",
                variant: "secondary",
                onClick: onCancel,
                children: "Cancel"
            })
        ]
    });
}
export function FactForm({ viewer, contractId, onCancel, onSaved }) {
    const qc = useQueryClient();
    const [measure, setMeasure] = useState('1');
    const [scope, setScope] = useState('');
    const [period, setPeriod] = useState('');
    const [unit, setUnit] = useState('');
    const [state, setState] = useState('1');
    const [value, setValue] = useState('');
    const [rationale, setRationale] = useState('');
    const [effective, setEffective] = useState('');
    const [decision, setDecision] = useState('');
    const retry = useRef();
    const date = measure === '3' || measure === '4';
    const save = useMutation({
        mutationFn: async ()=>{
            const payload = {
                contractId,
                key: new RegistryContractFactKey({
                    measure: Number(measure),
                    scope: scope.trim(),
                    period: period.trim(),
                    unit: date ? 'YYYY-MM-DD' : unit.trim()
                }),
                fact: new RegistryContractFactValue({
                    state: Number(state),
                    value: state !== '1' ? {
                        case: undefined
                    } : date ? {
                        case: 'date',
                        value
                    } : {
                        case: 'decimal',
                        value
                    }
                }),
                decision: Number(decision),
                rationale: rationale.trim(),
                effectiveAt: effective ? Timestamp.fromDate(new Date(`${effective}T00:00:00Z`)) : undefined
            };
            const signature = JSON.stringify(payload);
            if (retry.current?.signature !== signature) retry.current = {
                signature,
                key: crypto.randomUUID()
            };
            return contractsClient.writeRegistryContractFact({
                ...payload,
                retryKey: retry.current.key
            });
        },
        onSuccess: ()=>{
            void qc.invalidateQueries({
                queryKey: contractQueryKey(viewer)
            });
            onSaved();
        },
        retry: false
    });
    const submit = (e)=>{
        e.preventDefault();
        save.mutate();
    };
    return /*#__PURE__*/ _jsxs("form", {
        className: "space-y-3 rounded border p-4",
        "data-testid": ids.form,
        onSubmit: submit,
        children: [
            /*#__PURE__*/ _jsx("h3", {
                children: "Review a private scoped fact"
            }),
            /*#__PURE__*/ _jsx("p", {
                children: "Public ingestion is separate. A committed review supersedes only this exact measure, workshare, period and unit. Committing an earlier proposal is a new decision and receives a new retry key."
            }),
            /*#__PURE__*/ _jsx(Picker, {
                label: "Measure",
                value: measure,
                required: true,
                onChange: (v)=>{
                    setMeasure(v);
                    setValue('');
                },
                options: measures.map(([id, label])=>({
                        id: String(id),
                        label
                    }))
            }),
            /*#__PURE__*/ _jsx(Field, {
                label: "Scope / actual entity or workshare",
                children: /*#__PURE__*/ _jsx(SpecraInput, {
                    required: true,
                    value: scope,
                    onChange: (e)=>setScope(e.target.value),
                    placeholder: "e.g. prime or subcontract:Company UEI"
                })
            }),
            /*#__PURE__*/ _jsx(Field, {
                label: "Exact period",
                children: /*#__PURE__*/ _jsx(SpecraInput, {
                    required: true,
                    value: period,
                    onChange: (e)=>setPeriod(e.target.value)
                })
            }),
            /*#__PURE__*/ _jsx(Field, {
                label: "Unit",
                children: /*#__PURE__*/ _jsx(SpecraInput, {
                    required: true,
                    readOnly: date,
                    value: date ? 'YYYY-MM-DD' : unit,
                    onChange: (e)=>setUnit(e.target.value),
                    pattern: date ? undefined : '[A-Z]{3}',
                    placeholder: "USD"
                })
            }),
            /*#__PURE__*/ _jsx(Picker, {
                label: "Value state",
                value: state,
                required: true,
                onChange: setState,
                options: [
                    {
                        id: '1',
                        label: 'Known (including zero)'
                    },
                    {
                        id: '2',
                        label: 'Unknown'
                    },
                    {
                        id: '3',
                        label: 'Not applicable'
                    },
                    {
                        id: '4',
                        label: 'Retrieval failed'
                    }
                ]
            }),
            state === '1' && /*#__PURE__*/ _jsx(Field, {
                label: date ? 'Date (YYYY-MM-DD)' : 'Exact decimal amount',
                children: /*#__PURE__*/ _jsx(SpecraInput, {
                    required: true,
                    type: date ? 'date' : 'text',
                    inputMode: date ? undefined : 'decimal',
                    pattern: date ? undefined : '-?[0-9]+(\.[0-9]+)?',
                    value: value,
                    onChange: (e)=>setValue(e.target.value)
                })
            }),
            /*#__PURE__*/ _jsx(Field, {
                label: "Effective as of (if known)",
                children: /*#__PURE__*/ _jsx(SpecraInput, {
                    type: "date",
                    value: effective,
                    onChange: (e)=>setEffective(e.target.value)
                })
            }),
            /*#__PURE__*/ _jsx(Field, {
                label: "Rationale",
                children: /*#__PURE__*/ _jsx(SpecraInput, {
                    required: true,
                    value: rationale,
                    onChange: (e)=>setRationale(e.target.value)
                })
            }),
            /*#__PURE__*/ _jsx(Picker, {
                label: "Decision",
                value: decision,
                required: true,
                onChange: setDecision,
                options: [
                    {
                        id: String(RegistryContractDecision.PROPOSAL),
                        label: 'Proposal — does not change selected facts'
                    },
                    {
                        id: String(RegistryContractDecision.COMMIT),
                        label: 'Commit private reviewed fact'
                    }
                ]
            }),
            /*#__PURE__*/ _jsx(Failure, {
                error: save.error
            }),
            /*#__PURE__*/ _jsx(SpecraButton, {
                type: "submit",
                "data-testid": ids.save,
                disabled: save.isPending,
                children: save.isPending ? 'Saving decision…' : 'Save decision'
            }),
            /*#__PURE__*/ _jsx(SpecraButton, {
                type: "button",
                variant: "secondary",
                onClick: onCancel,
                children: "Cancel"
            })
        ]
    });
}
function RatePicker({ viewer, value, selected, onChange }) {
    const rates = useInfiniteQuery({
        queryKey: contractQueryKey(viewer, 'rate-options'),
        queryFn: ({ pageParam })=>loadRateOptions(viewer.tenant, pageParam),
        initialPageParam: 0,
        getNextPageParam: (page)=>page.nextOffset,
        placeholderData: undefined,
        retry: false
    });
    const options = rates.data?.pages.flatMap((p)=>p.options) ?? [];
    if (selected && !options.some((o)=>o.id === selected.id)) options.unshift(selected);
    return /*#__PURE__*/ _jsxs("div", {
        children: [
            /*#__PURE__*/ _jsx(Picker, {
                label: "Associated rate observation",
                value: value,
                onChange: onChange,
                options: options
            }),
            rates.isPending ? /*#__PURE__*/ _jsx("output", {
                children: "Loading rate choices…"
            }) : rates.isError ? /*#__PURE__*/ _jsxs("p", {
                role: "alert",
                children: [
                    "Rate choices unavailable. Existing associations are retained.",
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        type: "button",
                        onClick: ()=>void rates.refetch(),
                        children: "Retry rate choices"
                    })
                ]
            }) : /*#__PURE__*/ _jsx("p", {
                children: rates.hasNextPage ? 'Partial rate choices — load more to find additional rates.' : options.length ? 'All available rate choices loaded.' : 'No available rate choices returned.'
            }),
            rates.hasNextPage && /*#__PURE__*/ _jsx(SpecraButton, {
                type: "button",
                disabled: rates.isFetching,
                onClick: ()=>void rates.fetchNextPage(),
                children: "More rate choices"
            })
        ]
    });
}
